import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Modal, message, Space, Tag, Input, Form, InputNumber, Switch } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { OfferMerchant, AddMerchantRequest } from '../types/auth';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';

const { Search } = Input;
const { TextArea } = Input;

const WinterOffer: React.FC = () => {
  const [merchants, setMerchants] = useState<OfferMerchant[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddMerchantModalVisible, setIsAddMerchantModalVisible] = useState(false);
  const [addMerchantForm] = Form.useForm();
  const [isAddingMerchant, setIsAddingMerchant] = useState(false);
  const navigate = useNavigate();

  const loadMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getWinterOfferMerchants();
      setMerchants(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view winter offer merchants.');
        setMerchants([]);
      } else {
        message.error('Failed to load merchants');
        setMerchants([]);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    loadMerchants();
  }, [navigate, loadMerchants]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const filteredMerchants = merchants.filter(merchant =>
    merchant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewMerchant = (merchant: OfferMerchant) => {
    navigate(`/admin/winter/kids/${merchant.id}`);
  };

  const handleAddMerchant = () => {
    addMerchantForm.resetFields();
    setIsAddMerchantModalVisible(true);
  };

  const handleAddMerchantSubmit = async (values: any) => {
    setIsAddingMerchant(true);
    try {
      const request: AddMerchantRequest = {
        name: values.name,
        description: values.description || '',
        capacity: values.capacity,
        anyTime: values.anyTime || false,
        age: values.age || '',
        imageUrl: values.imageUrl || undefined
      };
      await apiService.addWinterOfferMerchant(request);
      message.success('Merchant added successfully');
      setIsAddMerchantModalVisible(false);
      addMerchantForm.resetFields();
      await loadMerchants();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to add merchants.');
      } else {
        message.error(error.response?.data?.msg || 'Failed to add merchant');
      }
    } finally {
      setIsAddingMerchant(false);
    }
  };


  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity',
      key: 'capacity',
      width: 100,
    },
    {
      title: 'Age',
      dataIndex: 'age',
      key: 'age',
      width: 100,
    },
    {
      title: 'Any Time',
      dataIndex: 'anyTime',
      key: 'anyTime',
      width: 100,
      render: (anyTime: boolean) => (
        <Tag color={anyTime ? 'green' : 'default'}>
          {anyTime ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: OfferMerchant) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewMerchant(record)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <Search
          placeholder="Search by merchant name"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          style={{ width: 300 }}
          onSearch={handleSearch}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={handleAddMerchant}
        >
          Add Merchant
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredMerchants}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} merchants`,
        }}
      />

      {/* Add Merchant Modal */}
      <Modal
        title="Add Merchant"
        open={isAddMerchantModalVisible}
        onCancel={() => {
          setIsAddMerchantModalVisible(false);
          addMerchantForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={addMerchantForm}
          layout="vertical"
          onFinish={handleAddMerchantSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter merchant name' }]}
          >
            <Input placeholder="Enter merchant name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} placeholder="Enter description" />
          </Form.Item>
          <Form.Item
            name="capacity"
            label="Capacity"
            rules={[{ required: true, message: 'Please enter capacity' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter capacity" />
          </Form.Item>
          <Form.Item
            name="age"
            label="Age"
          >
            <Input placeholder="Enter age range (e.g., 5-10)" />
          </Form.Item>
          <Form.Item
            name="imageUrl"
            label="Image URL"
            rules={[
              {
                type: 'url',
                message: 'Please enter a valid URL'
              }
            ]}
          >
            <Input placeholder="Enter image URL (optional)" />
          </Form.Item>
          <Form.Item
            name="anyTime"
            label="Any Time"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={isAddingMerchant}
              >
                Add Merchant
              </Button>
              <Button
                onClick={() => {
                  setIsAddMerchantModalVisible(false);
                  addMerchantForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WinterOffer;



